<template>
  <div class="Sunburst-graph"></div>
</template>

<script>
import {select} from 'd3-selection'
import {scaleLinear, scaleOrdinal} from 'd3-scale'
import {arc} from 'd3-shape'
import {hierarchy, partition} from 'd3-hierarchy'
import {hsl} from 'd3-color'
import {schemeCategory10} from 'd3-scale-chromatic'
import {interpolate} from 'd3-interpolate'
import {transition} from 'd3-transition'
import {palette} from './Palette'

const d3 = Object.assign(
  {},
  {
    select,
    scaleLinear,
    scaleOrdinal,
    arc,
    hierarchy,
    partition,
    hsl,
    schemeCategory10,
    interpolate,
    transition
  }
)

export default {
  props: {
    data: {
      type: Object,
      default: null
    },
    weights: {
      type: Object,
      default: null
    },
    nodeId: {
      type: [String, Number],
      default: null
    },
    size: {
      type: Number,
      default: 500
    }
  },
  data() {
    return {
      svg: null,
      x: null,
      y: null,
      arc: null,
      radius: null,
      ydomain: null,
      yrange: null,
      currentNode: null,
      color: null,
      nodesById: [],
      maxDepth: 0,
      maxValue: 0
    }
  },
  watch: {
    data: function() {
      this.nodesById = []
      this.maxDepth = 0
      this.maxValue = 0
      this.currentNode = null
      if (this.svg) {
        d3.select('.Sunburst-graph svg').remove()
        this.svg = null
      }
      this.regenerate()
    },
    nodeId: function() {
      let self = this
      let oldNode = this.currentNode
      let newNode = this.getNewCurrentNode()
      if (oldNode) {
        if (newNode) {
          if (
            this.isChild(oldNode, newNode) ||
            this.isChild(newNode, oldNode)
          ) {
            this.updateGraph(newNode)
          } else {
            let commonParent = this.getClosestParent(newNode, oldNode)
            this.updateGraph(commonParent, 500)
            setTimeout(function() {
              self.updateGraph(newNode)
            }, 500)
          }
        }
      } else {
        this.updateGraph(newNode, 0)
      }
    },
    weights: function() {
      this.updateGraph(this.currentNode, 500)
    }
  },
  mounted() {
    this.regenerate()
  },
  methods: {
    getNewCurrentNode: function() {
      let id = this.nodeId
      if (!id) {
        id = this.data._id
      }
      return this.nodesById[id]
    },

    getClosestParent: function(n1, n2) {
      let c = n1
      while (c.parent) {
        c = c.parent
        if (this.isChild(c, n2)) {
          return c
        }
      }
    },
    getDomain: function(arr) {
      const arr2 = []
      for (let i = 0; i < arr.length; i++) {
        arr2.push(i / (arr.length - 1))
      }
      return arr2
    },

    regenerate: function() {
      if (this.data) {
        // let data = JSON.parse(JSON.stringify(this.data.actors.root))
        if (!this.svg) {
          this.createGraph(this.data)
        }
        let node = this.getNewCurrentNode()
        if (node) {
          this.updateGraph(node, 0)
        }
      }
    },

    updateGraph: function(node, time = 750) {
      if (node) {
        this.currentNode = node
      }

      let r = this.radius
      let range = [0, r * 0.08, r * 0.4, r]
      // if on root, center node is bigger
      if (node.depth === 0) {
        range = [0, r * 0.15, r * 0.5, r]
      }

      // total max depth
      let maxDepth = this.maxDepth + 1

      // max depth of the current branch
      let localMaxDepth = maxDepth
      if (node.depth > 0) {
        localMaxDepth = this.getMaxDepth(node) + 1
      }

      // last ring
      let end = localMaxDepth / maxDepth
      // first ring
      let nd = node.depth / maxDepth
      // second ring
      let nd2 = (node.depth + 1) / maxDepth
      // if second ring is the last one (current node is last in branch)
      if (node.depth + 1 === localMaxDepth) {
        // get middle point between first ring and last
        nd2 = nd + (end - nd) / 2
      }

      // if on root, center node is first ring
      if (node.depth === 0) {
        nd = 1 / maxDepth
        nd2 = 2 / maxDepth
        // adjust when only one depth
        if (maxDepth === 2) {
          nd2 = nd + (end - nd) / 2
        }
      }

      let domain = [0, nd, nd2, end]
      this.maxValue = this.getMaxValue(node)
      this.yrange = range
      this.ydomain = domain

      this.svg
        .selectAll('path')
        .transition()
        .duration(time)
        .attrTween('d', this.arcTween(node))
        .style('fill', this.pathColor)
        .style('fill-opacity', this.pathOpacity)
        .style('stroke-opacity', this.strokeOpacity)

      this.svg
        .selectAll('text')
        .classed('hidden', this.textHidden)
        .transition()
        .duration(time)
        .attrTween('transform', this.textTransform)
    },

    textHidden: function(d) {
      return !(
        this.isChild(this.currentNode, d) &&
        d.depth - this.currentNode.depth < 2
      )
    },

    isChild: function(parent, child) {
      if (child === parent) {
        return true
      }
      let c = child.parent
      while (c) {
        if (c === parent) {
          return true
        }
        c = c.parent
      }
      return false
    },

    createGraph: function(data) {
      let self = this
      this.svg = d3
        .select('.Sunburst-graph')
        .append('svg')
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .attr('viewBox', '0 0 ' + this.size + ' ' + this.size)
        .append('g')
        .attr(
          'transform',
          'translate(' + this.size / 2 + ',' + this.size / 2 + ')'
        )

      let root = d3.hierarchy(data)
      root.sum(function(d) {
        if (d.size) {
          return d.size
        }
        // if (d.value) {
        //   return 10+d.value
        // }else {
        //   return 10
        // }
        // if (!d.children) {
        //   return d.value
        // }
      })

      let nodes = d3
        .partition()(root)
        .descendants()

      let enter = this.svg
        .selectAll('path')
        .data(nodes)
        .enter()

      enter
        .append('path')
        .on('click', this.onClick)
        .on('mouseover', this.onMouseOver)
        .on('mouseout', this.onMouseOut)
        .each(this.serializeNodes)
        .each(function(d, i, a) {
          d.path = d3.select(a[i])
        })

      enter
        .append('text')
        .attr('text-anchor', 'middle')
        .text(this.getText)
        .each(this.wrapText)
        .attr('class', function(d) {
          if (d.depth === 0) {
            return 'root'
          }
        })
        .each(function(d, i, a) {
          d.text = d3.select(a[i])
        })

      this.radius = this.size / 2 - 10

      this.ydomain = [0, 0.5, 1]
      this.yrange = [0, this.radius * 0.5, this.radius]

      let angleFrom = -Math.PI
      let angleTo = angleFrom + Math.PI * 2

      this.x = d3.scaleLinear().range([angleFrom, angleTo])
      this.y = d3
        .scaleLinear()
        .domain(this.ydomain)
        .range(this.yrange)

      this.color = d3.scaleOrdinal(d3.schemeCategory10)

      this.arc = d3
        .arc()
        .startAngle(function(d) {
          return Math.max(angleFrom, Math.min(angleTo, self.x(d.x0)))
        })
        .endAngle(function(d) {
          return Math.max(angleFrom, Math.min(angleTo, self.x(d.x1)))
        })
        .innerRadius(function(d) {
          return Math.max(0, self.y(d.y0))
        })
        .outerRadius(function(d) {
          return Math.max(0, self.y(d.y1))
        })
    },
    getText: function(d) {
      let name = d.data.name
      if (d.depth === 0) {
        let initials = name.match(/\b(\w)/g)
        if (initials.length === 2) {
          name = initials.join('.') + '.'
        } else {
          name = name.substr(0, 2)
        }
      }
      return name
    },
    getMaxDepth: function(n) {
      let depth = n.depth
      if (n.children) {
        for (let a of n.children) {
          let tmp = this.getMaxDepth(a)
          if (tmp > depth) {
            depth = tmp
          }
        }
      }
      return depth
    },
    getValue: function(id) {
      let v = 1
      if (this.weights) {
        v = 0
        if (this.weights[id]) {
          v = this.weights[id]
        }
      }
      return v
    },
    getMaxValue: function(d) {
      let value = this.getValue(d.data._id)
      if (d.children) {
        for (let a of d.children) {
          let tmp = this.getMaxValue(a)
          if (tmp > value) {
            value = tmp
          }
        }
      }
      return value
    },

    onMouseOver: function(d) {
      d.path.classed('hover', true)
      d.text.classed('hover', true)
      // if (d !== currentNode && (isChild(currentNode, d) || isChild(d, currentNode))) {
      //   d3.select('#path' + d.id).classed('hover', true)
      //   d3.select('#text' + d.id).classed('hover', true)
      // }
    },

    onMouseOut: function(d) {
      d.path.classed('hover', false)
      d.text.classed('hover', false)
    },

    onClick: function(d) {
      this.$router.push({params: {actor: d.data.pathArr}}).catch(() => {})
    },

    pathColor: function(d) {
      if (!this.weights) {
        return d.baseColor
      }
      let col = d.baseColor
      if (d.depth > 1) {
        col = d.parent.color
      }
      d.color = col
      return col
    },

    pathOpacity: function(d) {
      if (d.depth === 0) {
        return 0
      }
      if (
        !this.isChild(this.currentNode, d) &&
        !this.isChild(d, this.currentNode)
      ) {
        return 0
      }
      let v = 1
      if (this.weights) {
        v = this.getValue(d.data._id) / this.maxValue
        if (isNaN(v)) v = 0
      }
      return v * 0.9 + 0.1
    },

    strokeOpacity: function(d) {
      if (
        this.isChild(this.currentNode, d) ||
        this.isChild(d, this.currentNode)
      ) {
        return 0.3
      } else {
        return 0
      }
    },

    arcTween: function(d) {
      let self = this
      let domain = this.ydomain
      let range = this.yrange

      let xd = d3.interpolate(this.x.domain(), [d.x0, d.x1])
      let yd = d3.interpolate(this.y.domain(), domain)
      let yr = d3.interpolate(this.y.range(), range)

      return function(d, i) {
        if (i) {
          return function() {
            return self.arc(d)
          }
        } else {
          return function(t) {
            self.x.domain(xd(t))
            self.y.domain(yd(t)).range(yr(t))
            return self.arc(d)
          }
        }
      }
    },

    serializeNodes: function(d) {
      this.nodesById[d.data._id] = d

      if (d.depth > this.maxDepth) {
        this.maxDepth = d.depth
      }

      let num = 0
      if (d.parent) {
        for (let j in d.parent.children) {
          if (d.parent.children[j] === d) {
            num = j
          }
        }
      }
      d.colorIndex = '0'
      if (d.data.color) {
        d.colorIndex = d.data.color.split('color')[1]
      }
      let pal = palette[d.colorIndex]
      let col = '#ffffff'
      if (d.depth === 1) {
        col = pal(0.5)
      } else if (d.depth === 2) {
        let l = d.parent.children.length
        let index = num / (l - 1)
        if (!index) index = 0
        if (index > 1) index = 1

        col = pal(0.3 + index * 0.33)
      } else if (d.depth > 2) {
        col = d3.hsl(d.parent.baseColor).darker(num / 4)
      }
      d.baseColor = col
    },

    textTransform: function(d) {
      let self = this
      return function() {
        if (d.depth === 0) {
          return 'translate(0)'
        }
        return 'translate(' + self.arc.centroid(d) + ')'
      }
    },

    wrapText: function(d, i, a) {
      let text = d3.select(a[i])

      let words = text
        .text()
        .split('/')
        .join('/ ')
      words = words.split('&').join('& ')
      words = words.split(/\s+/).reverse()

      let line = []
      let lines = []
      let tspan = text.text(null).append('tspan')

      lines.push(tspan)
      while (words.length) {
        let word = words.pop()
        line.push(word)
        tspan.html(line.join(' ') + '&#8203;') // &#8203; is the zero width space, to fix capitalization
        if (tspan.node().getComputedTextLength() > 100 && line.length > 1) {
          line.pop()
          tspan.html(line.join(' ') + '&#8203;')
          line = [word]
          tspan = text.append('tspan').text(word)
          lines.push(tspan)
        }
      }

      let l = lines.length - 1
      for (let j in lines) {
        tspan = lines[j]
        let y = j - l / 2 + 0.25
        tspan.attr('x', 0).attr('y', y + 'em')
      }
    }
  }
}
</script>

<style>
.Sunburst-graph {
  position: relative;
  padding: 0;
  text-align: center;
  margin: auto;
  svg {
    user-select: none;
    path {
      stroke: #171717;
      opacity: 1;
      stroke-opacity: 0.3;
      transition: opacity linear 0.15s, stroke linear 0.75s;
      .depth1,
      .depth2 {
        stroke-opacity: 0;
      }
      &.hover {
        opacity: 0.8;
        transition: opacity linear 0.05s, stroke linear 0.75s;
      }
    }
    text {
      font-size: 14px;
      font-weight: 300;
      pointer-events: none;
      display: block;
      fill: black;
      visibility: visible;
      transition: opacity linear 0.75s;
      &.hidden {
        visibility: hidden;
        opacity: 0;
        transition: opacity linear 0.2s;
      }
      &.hover {
        visibility: visible;
        opacity: 1;
        transition: opacity linear 0.2s;
      }
    }
  }
}
</style>
